import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import financialApi from '../utils/financialApi';
import { RiskSection } from './FinancialAnalysisPage';

const btn = (bg = '#4F46E5', color = '#fff') => ({ padding: '8px 16px', borderRadius: 8, border: 'none', background: bg, color, fontSize: 13, fontWeight: 700, cursor: 'pointer' });
const inp = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, boxSizing: 'border-box' };

export default function RiskRegisterPage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const [access, setAccess] = useState(null);
  const [showManage, setShowManage] = useState(false);

  const load = useCallback(() => {
    financialApi.get(`/projects/${spaceId}/risk-access`).then(setAccess).catch(() => setAccess({ can_access: false, can_manage: false }));
  }, [spaceId]);
  useEffect(() => { load(); }, [load]);

  if (!access) return <div style={{ padding: 40, color: '#6B7280' }}>Loading…</div>;

  if (!access.can_access) {
    return (
      <div style={{ padding: 40, textAlign: 'center', maxWidth: 460, margin: '40px auto' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 8px' }}>Risk Register — no access</h2>
        <p style={{ fontSize: 13.5, color: '#6B7280', lineHeight: 1.6 }}>You haven't been assigned to this project's Risk Register. The project creator can grant you access.</p>
        <button onClick={() => navigate(-1)} style={{ ...btn('#F3F4F6', '#111827'), marginTop: 12 }}>← Back</button>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
        <button onClick={() => navigate(-1)} style={{ ...btn('#F3F4F6', '#111827'), padding: '6px 12px' }}>← Back</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 20, fontWeight: 850, margin: 0 }}>⚠️ Risk Register</h1>
          <div style={{ fontSize: 12.5, color: '#6B7280' }}>Project risks — impact, likelihood, mitigation, owner and RAG status.</div>
        </div>
        {access.can_manage && (
          <button onClick={() => setShowManage(true)} style={btn('#F3F4F6', '#4F46E5')}>👥 Manage Access</button>
        )}
      </div>

      <RiskSection spaceId={spaceId} canManage={!!access.can_edit} />

      {showManage && <ManageAccessModal spaceId={spaceId} onClose={() => setShowManage(false)} />}
    </div>
  );
}

function ManageAccessModal({ spaceId, onClose }) {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [pick, setPick] = useState('');
  const [level, setLevel] = useState('edit');
  const load = useCallback(() => {
    financialApi.get(`/projects/${spaceId}/risk-access/assignees`).then(setData).catch(() => setData({ owner: null, assignees: [] }));
  }, [spaceId]);
  useEffect(() => {
    load();
    financialApi.get(`/projects/${spaceId}/risks-users`).then(setUsers).catch(() => {
      financialApi.get(`/projects/${spaceId}/users`).then(setUsers).catch(() => setUsers([]));
    });
  }, [spaceId, load]);

  const add = async () => {
    if (!pick) return;
    try { await financialApi.post(`/projects/${spaceId}/risk-access/assignees`, { user_id: pick, access_level: level }); setPick(''); setLevel('edit'); load(); } catch (e) { /* noop */ }
  };
  const setUserLevel = async (uid, lvl) => {
    try { await financialApi.post(`/projects/${spaceId}/risk-access/assignees`, { user_id: uid, access_level: lvl }); load(); } catch (e) { /* noop */ }
  };
  const remove = async (uid) => {
    try { await financialApi.delete(`/projects/${spaceId}/risk-access/assignees/${uid}`); load(); } catch (e) { /* noop */ }
  };

  const assignedIds = new Set((data?.assignees || []).map(a => a.id));
  const ownerId = data?.owner?.id;
  const candidates = (users || []).filter(u => u.id !== ownerId && !assignedIds.has(u.id));

  const levelStyle = { padding: '5px 8px', borderRadius: 7, border: '1px solid #E5E7EB', fontSize: 12, background: '#fff' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, width: 560, maxWidth: '92vw', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>Risk Register Access</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#9CA3AF' }}>×</button>
        </div>
        <p style={{ fontSize: 12.5, color: '#6B7280', margin: '0 0 16px' }}>Assign people and choose whether they can edit risks or only view them. You (the creator) always have full access.</p>

        {data?.owner && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#F8F9FB', borderRadius: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{data.owner.full_name}</span>
            <span style={{ fontSize: 11, color: '#6B7280' }}>{data.owner.email}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#4F46E5', background: 'rgba(79,70,229,0.1)', padding: '3px 8px', borderRadius: 999 }}>Creator</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <select value={pick} onChange={e => setPick(e.target.value)} style={{ ...inp, flex: 1 }}>
            <option value="">— Select a person to assign —</option>
            {candidates.map(u => <option key={u.id} value={u.id}>{u.full_name}{u.email ? ` · ${u.email}` : ''}</option>)}
          </select>
          <select value={level} onChange={e => setLevel(e.target.value)} style={{ ...inp, width: 130 }}>
            <option value="edit">Can edit</option>
            <option value="view">View only</option>
          </select>
          <button onClick={add} disabled={!pick} style={btn()}>Assign</button>
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: '#9CA3AF', marginBottom: 8 }}>Assigned ({data?.assignees?.length || 0})</div>
        {(!data?.assignees || data.assignees.length === 0) && <div style={{ fontSize: 12.5, color: '#9CA3AF' }}>No one assigned yet.</div>}
        {(data?.assignees || []).map(a => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid #F1F1F4' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{a.full_name}</div>
              <div style={{ fontSize: 11, color: '#9CA3AF' }}>{a.email}</div>
            </div>
            <select value={a.access_level || 'edit'} onChange={e => setUserLevel(a.id, e.target.value)} style={{ ...levelStyle, marginLeft: 'auto' }}>
              <option value="edit">Can edit</option>
              <option value="view">View only</option>
            </select>
            <button onClick={() => remove(a.id)} style={{ ...btn('#FEF2F2', '#DC2626'), padding: '5px 12px', fontSize: 12 }}>Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}
